/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, TextFormat, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './insurance-claim.reducer';

export const InsuranceClaim = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const insuranceClaimList = useAppSelector(state => state.insuranceClaim.entities);
  const loading = useAppSelector(state => state.insuranceClaim.loading);
  const totalItems = useAppSelector(state => state.insuranceClaim.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="insurance-claim-heading" data-cy="InsuranceClaimHeading">
        <Translate contentKey="tinyMedicRestApp.insuranceClaim.home.title">Insurance Claims</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.insuranceClaim.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/insurance-claim/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.insuranceClaim.home.createLabel">Create new Insurance Claim</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.insuranceClaim.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {insuranceClaimList && insuranceClaimList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('claimCode')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.claimCode">Claim Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('claimCode')} />
                </th>
                <th className="hand" onClick={sort('dateFrom')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.dateFrom">Date From</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dateFrom')} />
                </th>
                <th className="hand" onClick={sort('dateTo')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.dateTo">Date To</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dateTo')} />
                </th>
                <th className="hand" onClick={sort('adjustment')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.adjustment">Adjustment</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('adjustment')} />
                </th>
                <th className="hand" onClick={sort('claimedTotal')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.claimedTotal">Claimed Total</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('claimedTotal')} />
                </th>
                <th className="hand" onClick={sort('approvedTotal')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.approvedTotal">Approved Total</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('approvedTotal')} />
                </th>
                <th className="hand" onClick={sort('dateProcessed')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.dateProcessed">Date Processed</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dateProcessed')} />
                </th>
                <th className="hand" onClick={sort('explanation')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.explanation">Explanation</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('explanation')} />
                </th>
                <th className="hand" onClick={sort('rejectionReason')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.rejectionReason">Rejection Reason</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('rejectionReason')} />
                </th>
                <th className="hand" onClick={sort('guaranteeId')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.guaranteeId">Guarantee Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('guaranteeId')} />
                </th>
                <th className="hand" onClick={sort('externalId')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.externalId">External Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('externalId')} />
                </th>
                <th className="hand" onClick={sort('type')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.type">Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('type')} />
                </th>
                <th className="hand" onClick={sort('status')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.status">Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('status')} />
                </th>
                <th className="hand" onClick={sort('paymentStatus')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.paymentStatus">Payment Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('paymentStatus')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('timestampEntry')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.timestampEntry">Timestamp Entry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampEntry')} />
                </th>
                <th className="hand" onClick={sort('lastUpdated')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.lastUpdated">Last Updated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastUpdated')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.policy">Policy</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.patient">Patient</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.location">Location</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.insuranceClaim.bill">Bill</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {insuranceClaimList.map((insuranceClaim, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/insurance-claim/${insuranceClaim.id}`} color="link" size="sm">
                      {insuranceClaim.id}
                    </Button>
                  </td>
                  <td>{insuranceClaim.claimCode}</td>
                  <td>{insuranceClaim.dateFrom}</td>
                  <td>{insuranceClaim.dateTo}</td>
                  <td>{insuranceClaim.adjustment}</td>
                  <td>{insuranceClaim.claimedTotal}</td>
                  <td>{insuranceClaim.approvedTotal}</td>
                  <td>{insuranceClaim.dateProcessed}</td>
                  <td>{insuranceClaim.explanation}</td>
                  <td>{insuranceClaim.rejectionReason}</td>
                  <td>{insuranceClaim.guaranteeId}</td>
                  <td>{insuranceClaim.externalId}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.PatientType.${insuranceClaim.type}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.InsuranceClaimStatus.${insuranceClaim.status}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.PaymentStatus.${insuranceClaim.paymentStatus}`} />
                  </td>
                  <td>{insuranceClaim.active ? 'true' : 'false'}</td>
                  <td>
                    {insuranceClaim.microSec ? <TextFormat type="date" value={insuranceClaim.microSec} format={APP_DATE_FORMAT} /> : null}
                  </td>
                  <td>
                    {insuranceClaim.timestampEntry ? (
                      <TextFormat type="date" value={insuranceClaim.timestampEntry} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {insuranceClaim.lastUpdated ? (
                      <TextFormat type="date" value={insuranceClaim.lastUpdated} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{insuranceClaim.cursor}</td>
                  <td>
                    {insuranceClaim.policy ? (
                      <Link to={`/insurance-policy/${insuranceClaim.policy.id}`}>{insuranceClaim.policy.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {insuranceClaim.patient ? <Link to={`/gluco-user/${insuranceClaim.patient.id}`}>{insuranceClaim.patient.id}</Link> : ''}
                  </td>
                  <td>
                    {insuranceClaim.location ? (
                      <Link to={`/hospital/${insuranceClaim.location.id}`}>{insuranceClaim.location.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {insuranceClaim.bill ? <Link to={`/hospital-bill/${insuranceClaim.bill.id}`}>{insuranceClaim.bill.id}</Link> : ''}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/insurance-claim/${insuranceClaim.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/insurance-claim/${insuranceClaim.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/insurance-claim/${insuranceClaim.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.insuranceClaim.home.notFound">No Insurance Claims found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={insuranceClaimList && insuranceClaimList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default InsuranceClaim;
